/**
 * The property card's CHROME SPEC -- frame, image box, details padding, price and countdown text -- as one leaf with
 * NO client directive, so two renderers read the same values: the interactive `PropertyCard` (client) and the explore
 * page's server-rendered preview card (`ExplorePreviewGrid`), which the app mounts over. Until 2026-09-17 these lived
 * inside the card's client modules, which a server component cannot import values from; a second spelling for the
 * preview would have drifted, and drift here is a visible jump at mount.
 *
 * DOES NOT SPREAD `PANEL_FRAME_RADIUS_STYLE` (`../common/layout-styles.ts`), and the decline is that preset's own,
 * measured, not a lapse here: it governs the EQUALITY arm only -- a body that IS the corner -- because containment at a
 * ONE-member floor is the shape this fleet declines four times over at TWO members (`INPUT_BASE_STYLE` 2/7,
 * `FILTER_INPUT` 2/9, `ITEM` 2/9, `menuButtonStyles` 2/7). The corner here is one member of a card's own six-member
 * chrome spec. FALSIFIER: a per-preset replay showing a one-member containment arm is FP-free across the UI tier.
 */

import { FULL_SIZE_STYLE, FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { FLEX_CENTER_STYLE, FLEX_COLUMN_STYLE, FLEX_ROW_START_STYLE } from '@federa/const/style-flex-presets';
import { colors } from '../css-refs.colors.ts';
import { spacing } from '../css-refs.spacing.ts';
import { typography } from '../css-refs.typography.ts';
import { layoutTokens } from '../css-refs.layout-tokens.ts';
import { borderRadius, boxShadow, motion } from '../css-refs.effects.ts';
import type { CSSProperties } from 'react';

const { card } = layoutTokens;

export function getCardStyles(isHovered: boolean, hoverable: boolean, hasOnClick: boolean): CSSProperties {
	return {
		...FLEX_COLUMN_STYLE,
		backgroundColor: colors.background.primary,
		borderRadius: borderRadius['2lg'],
		boxShadow: isHovered && hoverable ? boxShadow.lg : boxShadow.sm,
		cursor: hasOnClick ? 'pointer' : 'default',
		transition: `box-shadow ${motion.duration.fast} ${motion.easing.standard}`,
	};
}

export const IMAGE_CONTAINER_BASE_STYLES: CSSProperties = {
	position: 'relative',
	...FULL_WIDTH_STYLE,
	height: card.property.imageHeight,
	backgroundColor: colors.background.coolNeutral,
	borderRadius: `${borderRadius['2lg']} ${borderRadius['2lg']} 0 0`,
	overflow: 'hidden',
};

/**
 * The photo is an `<img>`, NOT a CSS `background-image`, and the difference is every off-screen card's bytes. A
 * background is fetched the moment its element exists -- the browser cannot defer it -- so the explore grid, which
 * renders every property at once, requested every card's cover on load: measured on production 2026-09-04, 196 image
 * fetches on /explore with 8 cards in view. An `<img loading="lazy">` is fetched when it nears the viewport and not
 * before. Absolutely positioned under the dots and arrows so the container keeps owning the size, the radius and the
 * overlay geometry exactly as it did.
 */
export const IMAGE_STYLES: CSSProperties = {
	position: 'absolute',
	inset: 0,
	...FULL_SIZE_STYLE,
	objectFit: 'cover',
};

export const NO_IMAGE_STYLES: CSSProperties = {
	...FLEX_CENTER_STYLE,
	position: 'absolute',
	inset: 0,
};

export const propertyCardTokens = {
	layout: {
		detailsPadding: {
			top: spacing[6],
			left: spacing[3],
			right: spacing[3],
			bottom: spacing[6],
		},
		detailsStackGap: spacing[2.5],
		addressRowGap: spacing[8],
		priceInlineGap: spacing[2],
		actionGroupGap: spacing[3],
	},
	text: {
		/** Shared style for price label and price amount (identical typography) */
		priceText: {
			fontSize: typography.fontSize.xs,
			fontWeight: typography.fontWeight.normal,
			color: colors.text.primary,
			lineHeight: typography.lineHeight.tight,
		},
	},
	countdown: {
		gap: spacing[1],
		fontSize: typography.fontSize.xs,
		fontWeight: typography.fontWeight.normal,
		letterSpacing: typography.letterSpacing.tertiary,
	},
} as const;

export const DETAILS_PADDING_STYLES: CSSProperties = {
	paddingTop: propertyCardTokens.layout.detailsPadding.top,
	paddingRight: propertyCardTokens.layout.detailsPadding.right,
	paddingBottom: propertyCardTokens.layout.detailsPadding.bottom,
	paddingLeft: propertyCardTokens.layout.detailsPadding.left,
	...FLEX_COLUMN_STYLE,
	gap: propertyCardTokens.layout.detailsStackGap,
};

export function getAddressRowStyles(hasActions: boolean): CSSProperties {
	return {
		...FLEX_ROW_START_STYLE,
		justifyContent: hasActions ? 'space-between' : 'flex-start',
		gap: propertyCardTokens.layout.addressRowGap,
		flexWrap: 'nowrap',
	};
}
